import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/hooks/useAuth';
import { useImageUpload } from '@/hooks/useImageUpload';
import supabase from '@/lib/supabase';

const categories = ['자유', '검증요청', '피해사례', '정보공유', '질문'];

interface PostData {
  id: number;
  title: string;
  content: string;
  category: string;
  author: string;
  user_id: string | null;
  image_url: string | null;
}

export default function BoardEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const { images, uploadError, uploadImage, removeImage, setInitialImages, getImageUrls, setUploadError } = useImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('자유');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    if (!id || authLoading) return;
    fetchPost();
  }, [id, authLoading]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const numericId = parseInt(id!, 10);
      const { data, error: fetchError } = await supabase
        .from('posts')
        .select('*')
        .eq('id', numericId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const post = data as PostData;

      if (user && post.user_id && post.user_id !== user.id) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      if (!user && post.user_id) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      setTitle(post.title);
      setContent(post.content);
      setCategory(post.category);

      if (post.image_url) {
        try {
          const urls = JSON.parse(post.image_url) as string[];
          if (Array.isArray(urls) && urls.length > 0) {
            setInitialImages(urls);
          }
        } catch {
          // invalid JSON in image_url, ignore
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('이미지 크기는 5MB 이하만 가능합니다.');
        continue;
      }
      await uploadImage(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInsertImageToContent = (url: string) => {
    const imageMarkdown = `\n![이미지](${url})\n`;
    setContent((prev) => prev + imageMarkdown);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !id) return;

    setError('');
    setSubmitting(true);

    try {
      const numericId = parseInt(id, 10);
      const imageUrls = getImageUrls();

      const { error: updateError } = await supabase
        .from('posts')
        .update({
          title: title.trim(),
          content: content.trim(),
          category,
          image_url: imageUrls.length > 0 ? JSON.stringify(imageUrls) : null,
        })
        .eq('id', numericId);

      if (updateError) throw updateError;

      navigate(`/board/${numericId}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '게시글 수정 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">게시글을 불러오는 중...</span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-foreground-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400" />
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">게시글을 찾을 수 없습니다</h2>
            <p className="text-sm text-foreground-500 mb-6">삭제되었거나 존재하지 않는 게시글입니다.</p>
            <Link to="/board" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              목록으로 돌아가기
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">수정 권한이 없습니다</h2>
            <p className="text-sm text-foreground-500 mb-6">본인이 작성한 게시글만 수정할 수 있습니다.</p>
            <Link to={`/board/${id}`} className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              게시글로 돌아가기
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">글 수정하기</h1>
            <p className="text-sm text-foreground-400">작성한 게시글을 수정할 수 있습니다</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-foreground-200 p-6 md:p-8">
            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">분류</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-white text-foreground-950 text-sm px-4 py-2.5 rounded-md border border-foreground-200 focus:outline-none focus:border-primary-500 cursor-pointer"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                className="w-full bg-white text-foreground-950 text-sm px-4 py-2.5 rounded-md border border-foreground-200 focus:outline-none focus:border-primary-500 placeholder:text-foreground-400"
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
                rows={12}
                className="w-full text-sm text-foreground-950 bg-background-50 border border-foreground-200 rounded-md px-4 py-3 resize-none focus:outline-none focus:border-primary-500 placeholder:text-foreground-400"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">이미지 첨부</label>
              <div className="border-2 border-dashed border-foreground-200 rounded-lg p-4 hover:border-primary-400 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-4 cursor-pointer text-foreground-500 hover:text-primary-500 transition-colors"
                >
                  <i className="ri-image-add-line text-2xl" />
                  <span className="text-sm font-medium">이미지 업로드</span>
                  <span className="text-xs text-foreground-400">JPG, PNG, GIF (최대 5MB)</span>
                </button>

                {uploadError && (
                  <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      {uploadError}
                    </p>
                  </div>
                )}

                {images.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {images.map((img) => (
                      <div key={img.path} className="relative group">
                        {img.uploading ? (
                          <div className="aspect-square bg-background-100 rounded-md flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : (
                          <>
                            <img
                              src={img.url}
                              alt="업로드 이미지"
                              className="aspect-square object-cover rounded-md w-full"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-md transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() => handleInsertImageToContent(img.url)}
                                className="w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                                title="본문에 삽입"
                              >
                                <i className="ri-file-copy-line text-xs text-foreground-700" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeImage(img.path)}
                                className="w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                                title="삭제"
                              >
                                <i className="ri-close-line text-xs text-red-500" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-foreground-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                취소
              </button>
              <button
                type="submit"
                disabled={!title.trim() || !content.trim() || submitting}
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                {submitting ? '수정 중...' : (
                  <>
                    <i className="ri-edit-line" />
                    수정하기
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}